/**
 * BirFlyPermit — the deterministic validation engine.
 * Runs identically in the browser (instant feedback while the pilot fills the form) and on the
 * server (authoritative re-check at submit time). Every rule emits a `Finding` — including a
 * `pass` finding when it holds — so the officer sees the full ladder, not just the problems.
 */
import { cardValidity, stayLength } from './tenant';
import { nameSimilarity, typedNameMatches } from './names';
import { BOND_CLAUSE_COUNT, type Finding, type GliderClass, type MrzResult, type PilotFields, type RiskTier, type Severity, type TenantConfig } from './types';

export interface RuleContext {
  today: string; // YYYY-MM-DD
  tenant: TenantConfig;
  /** Server-only lookups. When undefined, the rules that need them are skipped (not failed). */
  duplicates?: { activeCardForDoc: boolean; onBlocklist: boolean };
}

export const RULE_IDS: string[] = [
  'mrz.format',
  'mrz.checkdigits',
  'doc.expiry',
  'doc.duplicate',
  'doc.blocklist',
  'name.match',
  'visa.covers',
  'arrival.plausible',
  'ins.validity',
  'ins.covers',
  'medical.validity',
  'cert.recognised',
  'cert.holder',
  'cert.expiry',
  'glider.known',
  'glider.class',
  'photo.quality',
  'emergency.distinct',
  'declaration.complete',
  'phone.format',
  'dob.plausible',
  'stay.window',
];

function find(rule: string, severity: Severity, explanation: string, extra?: Partial<Finding>): Finding {
  return { rule, severity, explanation, ...extra };
}

const INDIAN_DOC_TYPES = new Set(['aadhaar', 'voter_id', 'driving_licence']);

// Glider class rank, low → high. DHV classes map to their EN equivalent for comparison only.
const CLASS_RANK: Record<Exclude<GliderClass, 'unknown'>, number> = {
  'EN-A': 0,
  'DHV-1': 0,
  'EN-B': 1,
  'DHV-1-2': 1,
  'EN-C': 2,
  'DHV-2': 2,
  'EN-D': 3,
  'DHV-2-3': 3,
  CCC: 4,
  'DHV-3': 4,
};

function digitsOnly(s: string): string {
  return s.replace(/\D/g, '');
}

function ageYears(dob: string, today: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dob);
  const t = /^(\d{4})-(\d{2})-(\d{2})$/.exec(today);
  if (!m || !t) return null;
  const [, by, bm, bd] = m.map(Number) as unknown as [number, number, number, number];
  const [, ty, tm, td] = t.map(Number) as unknown as [number, number, number, number];
  let age = ty - by;
  if (tm < bm || (tm === bm && td < bd)) age--;
  return age;
}

/** Run every rule against `fields`/`mrz` under `ctx`, returning the full findings ladder. */
export function runRules(fields: PilotFields, mrz: MrzResult | null, ctx: RuleContext): Finding[] {
  const { tenant, today } = ctx;
  // The card period IS the pilot's stay (feedback round 6: nothing is checked against the season —
  // a pilot pays per week of stay and must simply be covered for those days).
  const { validFrom: cardValidFrom, validTo: cardValidTo } = cardValidity(tenant, today, { from: fields.stayFrom, to: fields.stayTo });
  const cardPeriod = `${cardValidFrom}..${cardValidTo}`;
  const out: Finding[] = [];

  // ── stay.window (feedback round 3: the stay drives card validity and the per-week fee) ────
  {
    if (!fields.stayFrom || !fields.stayTo) {
      out.push(find('stay.window', 'fail', 'Stay dates are missing — tell us the first and last day you will be in Bir.'));
    } else if (fields.stayTo < fields.stayFrom) {
      out.push(find('stay.window', 'fail', 'The last day of the stay is before the first day.', { found: `${fields.stayFrom} → ${fields.stayTo}` }));
    } else if (fields.stayTo < today) {
      out.push(find('stay.window', 'fail', 'The stay has already ended.', { found: fields.stayTo }));
    } else {
      const len = stayLength(fields.stayFrom, fields.stayTo);
      out.push(find('stay.window', 'pass', `Stay of ${len.days} day${len.days === 1 ? '' : 's'} (${len.weeks} week${len.weeks === 1 ? '' : 's'}); the card covers ${cardPeriod}.`));
    }
  }

  // ── mrz.format / mrz.checkdigits ──────────────────────────────────────────
  if (fields.docType === 'passport') {
    const hasLines = !!(fields.mrzLine1 && fields.mrzLine2);
    if (!hasLines && !fields.mrzLine1 && !fields.mrzLine2 && !mrz) {
      // MRZ capture was removed from the form: no MRZ at all means no MRZ findings.
    } else if (!hasLines) {
      out.push(find('mrz.format', 'warn', 'MRZ not captured; officer will confirm by eye.'));
    } else if (!mrz || mrz.reason) {
      out.push(
        find('mrz.format', 'fail', `MRZ lines could not be read as a valid passport MRZ${mrz?.reason ? ` (${mrz.reason})` : ''}.`, {
          found: `${fields.mrzLine1} / ${fields.mrzLine2}`,
        }),
      );
    } else {
      out.push(find('mrz.format', 'pass', 'MRZ lines are well-formed.'));
      const allChecksPass = mrz.checks.docNo && mrz.checks.dob && mrz.checks.expiry && mrz.checks.composite;
      if (!allChecksPass) {
        const failedChecks = Object.entries(mrz.checks)
          .filter(([, ok]) => !ok)
          .map(([k]) => k);
        out.push(
          find('mrz.checkdigits', 'fail', 'One or more MRZ check digits do not match — the document number, DOB, expiry or composite line may have been mis-scanned or altered.', {
            found: failedChecks.join(', '),
          }),
        );
      } else {
        out.push(find('mrz.checkdigits', 'pass', 'All MRZ check digits match.'));
      }
    }
  }

  // ── doc.expiry ─────────────────────────────────────────────────────────────
  if (!fields.docExpiry) {
    if (fields.docType === 'passport') {
      out.push(find('doc.expiry', 'fail', 'Passport expiry date is missing.'));
    } else {
      out.push(find('doc.expiry', 'pass', 'No expiry date required for this document type.'));
    }
  } else if (fields.docExpiry < cardValidTo) {
    out.push(
      find('doc.expiry', 'fail', 'The document expires before the Free Flyer Card would, so it will not stay valid for the whole card period.', {
        expected: `>= ${cardValidTo}`,
        found: fields.docExpiry,
      }),
    );
  } else {
    out.push(find('doc.expiry', 'pass', 'Document is valid through the card period.'));
  }

  // ── doc.duplicate / doc.blocklist (server-only) ─────────────────────────────
  if (ctx.duplicates) {
    if (ctx.duplicates.activeCardForDoc) {
      out.push(find('doc.duplicate', 'fail', 'An active Free Flyer Card already exists for this document number.'));
    } else {
      out.push(find('doc.duplicate', 'pass', 'No active card exists for this document number.'));
    }
    if (ctx.duplicates.onBlocklist) {
      out.push(find('doc.blocklist', 'fail', 'This document or pilot is on the office blocklist.'));
    } else {
      out.push(find('doc.blocklist', 'pass', 'Not on the office blocklist.'));
    }
  }

  // ── name.match ───────────────────────────────────────────────────────────
  if (mrz && !mrz.reason && (mrz.surname || mrz.givenNames)) {
    const pilotName = `${fields.givenNames} ${fields.surname}`;
    const mrzName = `${mrz.givenNames} ${mrz.surname}`;
    const sim = nameSimilarity(pilotName, mrzName);
    if (sim >= tenant.rules.nameMatchPass) {
      out.push(find('name.match', 'pass', 'Form name matches the MRZ name.', { found: sim.toFixed(2) }));
    } else if (sim >= tenant.rules.nameMatchWarn) {
      out.push(
        find('name.match', 'warn', 'Form name is close to, but not an exact match for, the MRZ name — officer should confirm by eye.', {
          expected: mrzName,
          found: pilotName,
        }),
      );
    } else {
      out.push(
        find('name.match', 'fail', 'Form name does not match the MRZ name.', { expected: mrzName, found: pilotName }),
      );
    }
  }

  // ── visa.covers / arrival.plausible ─────────────────────────────────────────
  if (fields.isForeign) {
    if (!fields.visaValidFrom || !fields.visaValidTo) {
      out.push(find('visa.covers', 'warn', 'Visa details not provided — officer will check the e-visa.'));
    } else if (fields.visaValidFrom > cardValidFrom || fields.visaValidTo < cardValidTo) {
      out.push(
        find('visa.covers', 'fail', 'The visa does not cover the stay dates (the card period) — it must be valid on every day of the stay.', {
          expected: cardPeriod,
          found: `${fields.visaValidFrom}..${fields.visaValidTo}`,
        }),
      );
    } else {
      out.push(find('visa.covers', 'pass', `Visa covers the stay (${cardPeriod}).`));
    }

    if (fields.arrivalDate) {
      if (fields.arrivalDate > today) {
        out.push(find('arrival.plausible', 'warn', 'Arrival date is in the future.', { found: fields.arrivalDate }));
      } else if (fields.visaValidFrom && fields.arrivalDate < fields.visaValidFrom) {
        out.push(
          find('arrival.plausible', 'warn', 'Arrival date is before the visa becomes valid.', {
            expected: `>= ${fields.visaValidFrom}`,
            found: fields.arrivalDate,
          }),
        );
      } else {
        out.push(find('arrival.plausible', 'pass', 'Arrival date is plausible.'));
      }
    }
  }

  // ── ins.validity / ins.covers ────────────────────────────────────────────
  if (!fields.insValidTo) {
    out.push(find('ins.validity', 'fail', 'Insurance valid-to date is missing.'));
  } else if (fields.insValidTo < cardValidTo) {
    out.push(
      find('ins.validity', 'fail', 'Insurance expires before the stay ends (the card period).', { expected: `>= ${cardValidTo}`, found: fields.insValidTo }),
    );
  } else {
    out.push(find('ins.validity', 'pass', 'Insurance covers the stay (card period).'));
  }

  {
    const text = fields.insCoverText.toLowerCase();
    const phrase = tenant.rules.insuranceCoverPhrases.find((p) => text.includes(p.toLowerCase()));
    if (phrase) {
      const idx = text.indexOf(phrase.toLowerCase());
      const start = Math.max(0, idx - 40);
      const end = Math.min(fields.insCoverText.length, idx + phrase.length + 40);
      out.push(find('ins.covers', 'pass', 'Insurance wording mentions paragliding/adventure-sport cover.', { evidence: fields.insCoverText.slice(start, end) }));
    } else {
      out.push(find('ins.covers', 'warn', 'Insurance wording does not clearly mention paragliding or adventure-sport cover — officer should confirm with the provider.'));
    }
  }

  // ── medical.validity ─────────────────────────────────────────────────────
  // The medical certificate is no longer asked for; only check it when a date is present.
  if (fields.medicalValidTo) {
    if (fields.medicalValidTo < cardValidTo) {
      out.push(
        find('medical.validity', 'warn', 'Medical certificate expires before the card would.', {
          expected: `>= ${cardValidTo}`,
          found: fields.medicalValidTo,
        }),
      );
    } else {
      out.push(find('medical.validity', 'pass', 'Medical certificate covers the card period.'));
    }
  }

  // ── cert.recognised / cert.holder / cert.expiry ─────────────────────────────
  const bodyOk = tenant.rules.recognisedCertBodies.includes(fields.certBody);
  const levelOk = tenant.rules.certLevels.includes(fields.certLevel);
  if (!bodyOk || !levelOk) {
    out.push(
      find('cert.recognised', 'fail', 'Certifying body or certification level is not on the recognised list.', {
        found: `${fields.certBody} / ${fields.certLevel}`,
      }),
    );
  } else {
    out.push(find('cert.recognised', 'pass', 'Certifying body and level are recognised.'));
  }

  if (fields.certHolderName) {
    const sim = nameSimilarity(fields.certHolderName, `${fields.givenNames} ${fields.surname}`);
    if (sim < tenant.rules.nameMatchWarn) {
      out.push(
        find('cert.holder', 'warn', 'Name on the certificate does not clearly match the pilot name.', {
          expected: `${fields.givenNames} ${fields.surname}`,
          found: fields.certHolderName,
        }),
      );
    } else {
      out.push(find('cert.holder', 'pass', 'Certificate holder name matches the pilot.'));
    }
  }

  if (fields.certExpiry) {
    if (fields.certExpiry < today) {
      out.push(find('cert.expiry', 'fail', 'Certification has expired.', { expected: `>= ${today}`, found: fields.certExpiry }));
    } else {
      out.push(find('cert.expiry', 'pass', 'Certification has not expired.'));
    }
  }

  // ── glider.known / glider.class ─────────────────────────────────────────────
  if (!fields.gliderModel) {
    out.push(find('glider.known', 'warn', 'Glider model not stated.'));
  } else {
    out.push(find('glider.known', 'pass', 'Glider model stated.'));
  }

  if (fields.gliderClass === 'unknown') {
    out.push(find('glider.class', 'warn', 'Glider class not stated.'));
  } else {
    const maxLabel = tenant.rules.gliderClassMaxByLevel[fields.certLevel];
    const maxRank = maxLabel ? CLASS_RANK[maxLabel as Exclude<GliderClass, 'unknown'>] : undefined;
    const rank = CLASS_RANK[fields.gliderClass];
    if (maxRank !== undefined && rank > maxRank) {
      out.push(
        find('glider.class', 'warn', `Glider class (${fields.gliderClass}) exceeds what is typical for certification level ${fields.certLevel} (${maxLabel}).`, {
          expected: `<= ${maxLabel}`,
          found: fields.gliderClass,
        }),
      );
    } else {
      out.push(find('glider.class', 'pass', 'Glider class is within what is typical for this certification level.'));
    }
  }

  // ── emergency.distinct ────────────────────────────────────────────────────
  if (!fields.emgPhone) {
    out.push(find('emergency.distinct', 'fail', 'Emergency contact phone is missing.'));
  } else if (digitsOnly(fields.emgPhone) !== '' && digitsOnly(fields.emgPhone) === digitsOnly(fields.phone)) {
    out.push(find('emergency.distinct', 'fail', "Emergency contact number is the same as the pilot's own number.", { found: fields.emgPhone }));
  } else {
    out.push(find('emergency.distinct', 'pass', 'Emergency contact number is distinct from the pilot number.'));
  }

  // ── declaration.complete ──────────────────────────────────────────────────
  {
    const declOk = fields.declarationAccepted.length === 4 && fields.declarationAccepted.every(Boolean);
    // Applications submitted before 17 Oct 2026 accepted the seven printed clauses; clause VIII
    // (no-fly areas) was added that day. Re-running the checks on them must not fail the bond.
    const bondOk = fields.bondAccepted.length >= BOND_CLAUSE_COUNT - 1 && fields.bondAccepted.length <= BOND_CLAUSE_COUNT && fields.bondAccepted.every(Boolean);
    const signatureOk = typedNameMatches(fields.typedName, `${fields.givenNames} ${fields.surname}`, tenant.rules.nameMatchWarn);
    const missing: string[] = [];
    if (!declOk) missing.push('declaration clauses');
    if (!bondOk) missing.push('indemnity bond clauses');
    if (!signatureOk) missing.push('typed signature does not match name');
    if (!fields.consentPurpose) missing.push('data-use consent');
    if (missing.length > 0) {
      out.push(find('declaration.complete', 'fail', 'Declaration is incomplete.', { found: missing.join('; ') }));
    } else {
      out.push(find('declaration.complete', 'pass', 'Declaration, bond and consent are complete.'));
    }
  }

  // ── phone.format ───────────────────────────────────────────────────────
  if (!/^\+[1-9]\d{6,14}$/.test(fields.phone)) {
    out.push(find('phone.format', 'fail', 'Phone number is not in international format (e.g. +919812345678).', { found: fields.phone }));
  } else {
    out.push(find('phone.format', 'pass', 'Phone number is in international format.'));
  }

  // ── dob.plausible ─────────────────────────────────────────────────────────
  {
    const age = ageYears(fields.dob, today);
    if (age === null) {
      out.push(find('dob.plausible', 'fail', 'Date of birth is missing or invalid.'));
    } else if (age < 16 || age > 90) {
      out.push(find('dob.plausible', 'fail', 'Age is outside the plausible range for a paraglider pilot (16-90).', { found: String(age) }));
    } else {
      out.push(find('dob.plausible', 'pass', 'Age is plausible.'));
    }
  }

  return out;
}

/** What the browser measured on the card photo. `faces` is null when the browser has no face detector. */
export interface PhotoQuality {
  faces: number | null;
  /** Laplacian-variance blur score in [0, 1] (apps/web/src/lib/image.ts `sharpnessScore`). */
  sharpness: number;
  /** Mean luma of the face region in [0, 1] (`brightnessScore`). */
  brightness: number;
}

/** Below this the photo is reported as blurry (Laplacian variance ≈ 200 on the 600×771 crop). Advisory only. */
export const PHOTO_SHARPNESS_WARN = 0.05;
/** Face-region brightness outside this band is reported as too dark / too bright. Advisory only. */
export const PHOTO_BRIGHTNESS_MIN = 0.12;
export const PHOTO_BRIGHTNESS_MAX = 0.95;

/**
 * `photo.quality` is computed in the browser from face/sharpness/brightness analysis of the uploaded photo.
 * Feedback round 6: only "no face at all" is a `fail` (and the register step refuses to continue on
 * a fail — the pilot retakes); everything else is a tip. Blur/lighting thresholds are loose
 * because selfies on a white wall or in a room measure far from a studio photo yet print fine.
 */
export function photoFinding(quality: PhotoQuality | null): Finding {
  const rule = 'photo.quality';
  if (!quality) {
    return find(rule, 'warn', 'Photo quality was not assessed — the office will check the photo.');
  }
  if (quality.faces === 0) {
    return find(rule, 'fail', 'No face was found in the photo. Retake it with your face centred in the frame, looking at the camera, in good light.', { found: 'faces=0' });
  }
  const issues: string[] = [];
  if (quality.faces !== null && quality.faces > 1) issues.push('more than one face appears — only you should be in the frame');
  if (quality.sharpness < PHOTO_SHARPNESS_WARN) issues.push('it looks blurry — hold the phone still');
  if (quality.brightness < PHOTO_BRIGHTNESS_MIN) issues.push('your face is very dark — face a window or a light');
  else if (quality.brightness > PHOTO_BRIGHTNESS_MAX) issues.push('your face is very bright — move out of direct light');
  if (issues.length > 0) {
    return find(rule, 'warn', `You can continue, but a better photo helps the office: ${issues.join('; ')}.`, {
      found: `faces=${quality.faces ?? 'n/a'}, sharpness=${quality.sharpness.toFixed(2)}, brightness=${quality.brightness.toFixed(2)}`,
    });
  }
  return find(rule, 'pass', quality.faces === null ? 'Photo looks usable (this browser cannot check for a face; the office will).' : 'Photo quality looks good.');
}

/** Overall risk tier: any fail → red; else any warn → amber; else green. */
export function riskTier(findings: Finding[]): RiskTier {
  if (findings.some((f) => f.severity === 'fail')) return 'red';
  if (findings.some((f) => f.severity === 'warn')) return 'amber';
  return 'green';
}

/** Plain-language list of what to fix, drawn from the `fail` findings (deduplicated, in order). */
export function correctionItems(findings: Finding[]): string[] {
  const seen = new Set<string>();
  const items: string[] = [];
  for (const f of findings) {
    if (f.severity !== 'fail') continue;
    if (seen.has(f.explanation)) continue;
    seen.add(f.explanation);
    items.push(f.explanation);
  }
  return items;
}
